/**
 * threadSections — the ONE sectioning rule for the thread lists (chat-thread
 * experience PR-5, 2026-09-09; built-ins first-class 2026-09-09). The rail,
 * the mobile sheet and the docked panel all derive their sections here, so a
 * thread lands in the same section on every surface.
 *
 * The viewer's ORDERED section list (custom categories + the three built-ins,
 * `withBuiltinSections`) decides the order top to bottom. A thread is in
 * EXACTLY ONE section: routines (the caller excludes them first — the
 * routines union is the rail's own) > the section it is FILED under (a
 * custom id or a built-in id on its own index doc; a folded alias follows)
 * > its NATURAL built-in (direct message / group thread / thread).
 *
 * A section renders only when non-empty (`includeEmpty` for the manager);
 * ordering WITHIN a section is the caller's (`orderThreads`).
 */
import {
  builtinIdForKind,
  categoryLabel,
  categoryOf,
  withBuiltinSections,
  type BuiltinSectionKind,
  type ThreadCategory,
  type ThreadListLang,
} from '../../types/threadCategories';
import type { ThreadDisplayKind } from '../../services/groupThreads/threadDisplayTitle';

export type ThreadSectionKind = 'category' | 'direct' | 'group' | 'threads';

export interface ThreadSection<T> {
  /** Stable key: `category:<id>` | 'direct' | 'group' | 'threads'. */
  key: string;
  kind: ThreadSectionKind;
  /** The label the viewer sees (their name, or the built-in's default in their language). */
  label: string;
  rows: T[];
  /** The section's id — a custom id, or the built-in's fixed id. */
  categoryId: string;
  builtin?: BuiltinSectionKind;
}

/** The English defaults (kept for callers that render a built-in's label without a viewer). */
export const THREAD_SECTION_LABELS: Record<Exclude<ThreadSectionKind, 'category'>, string> = {
  direct: 'direct messages',
  group: 'group threads',
  threads: 'threads',
};

export interface BuildThreadSectionsArgs<T> {
  /** Non-routine rows, already ordered. */
  rows: readonly T[];
  categories?: readonly ThreadCategory[] | null;
  /** The display kind of a row (resolveThreadDisplayKind at the call site). */
  kindOf: (row: T) => ThreadDisplayKind;
  lang?: ThreadListLang;
  /** Custom ids that fold into a built-in (see parseThreadCategories). */
  foldAliases?: Readonly<Record<string, string>> | null;
  /** Also return empty sections (the sections manager). */
  includeEmpty?: boolean;
}

function kindOfBuiltin(builtin: BuiltinSectionKind): ThreadSectionKind {
  return builtin === 'direct' ? 'direct' : builtin === 'group' ? 'group' : 'threads';
}

export function buildThreadSections<T extends { id: string; category?: string }>(
  args: BuildThreadSectionsArgs<T>,
): ThreadSection<T>[] {
  const items = withBuiltinSections(args.categories);
  const lang = args.lang ?? 'en';
  const bucket = new Map<string, T[]>();
  for (const item of items) bucket.set(item.id, []);
  for (const row of args.rows) {
    if (!row) continue;
    const filed = categoryOf(items, row.category, args.foldAliases);
    const id = filed ? filed.id : builtinIdForKind(args.kindOf(row));
    const list = bucket.get(id);
    if (list) list.push(row);
    else bucket.set(id, [row]);
  }
  const out: ThreadSection<T>[] = [];
  for (const item of items) {
    const rows = bucket.get(item.id) ?? [];
    if (rows.length === 0 && !args.includeEmpty) continue;
    if (item.builtin) {
      const kind = kindOfBuiltin(item.builtin);
      out.push({ key: kind, kind, label: categoryLabel(item, lang), rows, categoryId: item.id, builtin: item.builtin });
    } else {
      out.push({ key: `category:${item.id}`, kind: 'category', label: categoryLabel(item, lang), rows, categoryId: item.id });
    }
  }
  return out;
}

/** The stable `data-sidebar-section` / `data-sheet-section` value for a section. */
export function sidebarSectionHook(section: Pick<ThreadSection<unknown>, 'kind' | 'categoryId'>): string {
  if (section.kind === 'category') return `category-${section.categoryId ?? ''}`;
  if (section.kind === 'direct') return 'direct-messages';
  if (section.kind === 'group') return 'group-threads';
  return 'threads';
}

/** The type glyph a built-in section header wears. */
export function sectionGlyphKind(kind: ThreadSectionKind): 'direct' | 'group' | 'individual' {
  if (kind === 'direct') return 'direct';
  if (kind === 'group') return 'group';
  return 'individual';
}
